/**
 * Home screen.
 *
 * Answers, in order: how much is there in total, how is it split by purpose,
 * where does it physically sit, what is owed between funds, and what happened
 * recently. One request assembles all of it — see routes/dashboard.ts.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowLeftRight, CalendarClock, ListOrdered, TrendingDown, TrendingUp, Wallet } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/misc';
import { CardSkeleton, EmptyState, ErrorState, RowSkeleton } from '@/components/common';
import { FundCard } from '@/components/FundCard';
import { MoneyMap } from '@/components/MoneyMap';
import { LoansPanel } from '@/components/LoanCard';
import { TransactionRow } from '@/components/TransactionRow';
import { RepayLoanDialog } from '@/components/RepayLoanDialog';
import { MovementDialog, type MovementMode } from '@/components/MovementDialog';
import { QuickAddSheet } from '@/components/QuickAddSheet';
import { VoidDialog } from '@/components/VoidDialog';
import { formatDate, formatMonth, formatMoney } from '@/lib/format';
import { useDashboard, useFunds, useUpcoming } from '@/hooks/useApi';
import type { Loan, Transaction } from '@/lib/types';

export function DashboardPage(): JSX.Element {
  const { data, isLoading, error, refetch } = useDashboard();
  const { data: funds = [] } = useFunds();
  const { data: upcoming } = useUpcoming(30);

  const [repayLoan, setRepayLoan] = useState<Loan | null>(null);
  const [movement, setMovement] = useState<MovementMode | null>(null);
  const [quickAdd, setQuickAdd] = useState(false);
  const [voidTarget, setVoidTarget] = useState<Transaction | null>(null);

  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  if (isLoading || !data) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-28 w-full rounded-lg" />
        <CardSkeleton />
        <RowSkeleton count={4} />
      </div>
    );
  }

  const { total, thisMonth, previousMonthComparison, baseCurrency, baseDecimals } = data;
  const netDelta = thisMonth.netMinor - previousMonthComparison.netMinor;
  const hasAnything = data.funds.length > 0;

  return (
    <div className="space-y-6">
      {/* ── Headline ── */}
      <Card className="overflow-hidden">
        <CardContent className="p-5 sm:p-6">
          <p className="text-sm text-muted-foreground">Общо във всички каси</p>
          <p className="tabular mt-1 text-4xl font-semibold tracking-tight">
            {formatMoney(total.baseMinor, baseCurrency, baseDecimals)}
          </p>

          {total.unrealisedMinor !== 0 ? (
            <p className="mt-1 text-sm text-muted-foreground">
              включително{' '}
              <span
                className={cn(
                  'tabular',
                  total.unrealisedMinor > 0 ? 'text-income' : 'text-expense',
                )}
              >
                {total.unrealisedMinor > 0 ? '+' : '−'}
                {formatMoney(Math.abs(total.unrealisedMinor), baseCurrency, baseDecimals)}
              </span>{' '}
              нереализирана валутна разлика
            </p>
          ) : null}

          <div className="mt-5 grid gap-3 sm:grid-cols-3">
            <MonthStat
              label={`Приходи ${formatMonth(thisMonth.month)}`}
              minor={thisMonth.incomeMinor}
              currency={baseCurrency}
              decimals={baseDecimals}
              tone="income"
              icon={TrendingUp}
            />
            <MonthStat
              label="Разходи"
              minor={thisMonth.expenseMinor}
              currency={baseCurrency}
              decimals={baseDecimals}
              tone="expense"
              icon={TrendingDown}
            />
            <div className="rounded-md border p-3">
              <p className="text-xs text-muted-foreground">Нето</p>
              <p
                className={cn(
                  'tabular mt-0.5 text-lg font-semibold',
                  thisMonth.netMinor > 0 ? 'text-income' : thisMonth.netMinor < 0 ? 'text-expense' : '',
                )}
              >
                {thisMonth.netMinor > 0 ? '+' : thisMonth.netMinor < 0 ? '−' : ''}
                {formatMoney(Math.abs(thisMonth.netMinor), baseCurrency, baseDecimals)}
              </p>
              {netDelta !== 0 ? (
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {netDelta > 0 ? '↑' : '↓'} {formatMoney(Math.abs(netDelta), baseCurrency, baseDecimals)}{' '}
                  спрямо {formatMonth(previousMonthComparison.month)}
                </p>
              ) : null}
            </div>
          </div>
        </CardContent>
      </Card>

      {!hasAnything ? (
        <Card>
          <EmptyState
            icon={Wallet}
            title="Още няма нищо записано"
            description="Добави първата операция и касите ще започнат да се пълнят. Настрой касите и местата от Настройки, ако seed-ът не ги е създал."
            action={<Button onClick={() => setQuickAdd(true)}>Добави операция</Button>}
          />
        </Card>
      ) : null}

      {/* ── What is coming ── */}
      {upcoming && (upcoming.items.length > 0 || upcoming.overdueCount > 0) ? (
        <Card className={cn(upcoming.overdueCount > 0 && 'border-destructive/40')}>
          <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
            <div className="flex min-w-0 items-center gap-3">
              {upcoming.overdueCount > 0 ? (
                <AlertTriangle className="h-5 w-5 shrink-0 text-destructive" />
              ) : (
                <CalendarClock className="h-5 w-5 shrink-0 text-muted-foreground" />
              )}
              <div className="min-w-0">
                {upcoming.overdueCount > 0 ? (
                  <p className="text-sm font-medium text-destructive">
                    {upcoming.overdueCount} просрочени ·{' '}
                    {formatMoney(upcoming.overdueMinor, baseCurrency, baseDecimals)}
                  </p>
                ) : null}
                <p className="text-sm">
                  Предстоящи 30 дни:{' '}
                  <span className="tabular font-medium">
                    {formatMoney(upcoming.dueSoonMinor, baseCurrency, baseDecimals)}
                  </span>
                  <span className="text-muted-foreground">
                    {' '}· {upcoming.items.filter((i) => !i.overdue).length} плащания
                  </span>
                </p>
                {upcoming.items[0] ? (
                  <p className="truncate text-xs text-muted-foreground">
                    следващо: {upcoming.items[0].name} на {formatDate(upcoming.items[0].dueDate)}
                  </p>
                ) : null}
              </div>
            </div>
            <Button variant="outline" size="sm" asChild>
              <Link to="/plan">Към плана</Link>
            </Button>
          </CardContent>
        </Card>
      ) : null}

      {/* ── Loans ── */}
      <LoansPanel loans={data.loans} onRepay={setRepayLoan} />

      {/* ── Funds ── */}
      {hasAnything ? (
        <section>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="font-semibold">Каси</h2>
            <Button
              variant="ghost"
              size="sm"
              className="gap-2"
              onClick={() => setMovement('transfer')}
            >
              <ArrowLeftRight className="h-4 w-4" />
              Прехвърли
            </Button>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {data.funds.map((f) => (
              <FundCard
                key={f.fundId}
                balance={f}
                fund={funds.find((x) => x.id === f.fundId)}
                sparkline={data.sparklines[f.fundId]}
                baseCurrency={baseCurrency}
                baseDecimals={baseDecimals}
              />
            ))}
          </div>
        </section>
      ) : null}

      {/* ── Where the money is ── */}
      {hasAnything ? (
        <MoneyMap
          locations={data.byLocation}
          baseCurrency={baseCurrency}
          baseDecimals={baseDecimals}
          totalMinor={total.baseMinor}
        />
      ) : null}

      {/* ── Recent ── */}
      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
          <CardTitle className="text-base">Последни операции</CardTitle>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/transactions" className="gap-1">
              Всички
              <ListOrdered className="h-3.5 w-3.5" />
            </Link>
          </Button>
        </CardHeader>
        <CardContent>
          {data.recent.length === 0 ? (
            <EmptyState
              icon={ListOrdered}
              title="Няма операции"
              description="Първата ще се появи тук веднага след като я запишеш."
              action={<Button onClick={() => setQuickAdd(true)}>Добави операция</Button>}
            />
          ) : (
            <div className="space-y-2">
              {data.recent.map((tx) => (
                <TransactionRow key={tx.id} tx={tx} onVoid={setVoidTarget} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <RepayLoanDialog loan={repayLoan} onClose={() => setRepayLoan(null)} />
      <MovementDialog mode={movement} onClose={() => setMovement(null)} />
      <QuickAddSheet open={quickAdd} onOpenChange={setQuickAdd} />
      <VoidDialog tx={voidTarget} onClose={() => setVoidTarget(null)} />
    </div>
  );
}

function MonthStat({
  label,
  minor,
  currency,
  decimals,
  tone,
  icon: Icon,
}: {
  label: string;
  minor: number;
  currency: string;
  decimals: number;
  tone: 'income' | 'expense';
  icon: React.ComponentType<{ className?: string }>;
}): JSX.Element {
  return (
    <div className="rounded-md border p-3">
      <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Icon className="h-3.5 w-3.5" />
        <span className="truncate">{label}</span>
      </p>
      <p
        className={cn(
          'tabular mt-0.5 text-lg font-semibold',
          tone === 'income' ? 'text-income' : 'text-expense',
        )}
      >
        {formatMoney(minor, currency, decimals)}
      </p>
    </div>
  );
}
